import { useActiveAddress } from "@arweave-wallet-kit/react"
import { createDataItemSigner } from "@permaweb/aoconnect"

import { useQuery } from "@tanstack/react-query"

import { gql } from "urql"

import { AoInstance, Message, useAo } from "./ao"
import { AO_MIN_INGESTED_AT, goldsky } from "./graphql-client"
import { getLinkedMessages } from "./messages-api"
import { SwapRouteResult } from "./multihop-router"
import { getPoolByTokens } from "./router"
import { TokenInfo, transfer } from "./token"
import ENV from "../env"
import usePoolDetails from "../hooks/use-pool-details"
import { getDataSource } from "../services/data-source"
import { MAX_TRIES, RETRY_INTERVAL, AMM_FACTORY } from "../settings"
// Removed supabase client import; using fetch instead
import { PoolPnlHistory, TransactionsResponse, AoMessage } from "../types"
import { Tag, flattenTags, getTagValue } from "../utils/arweave"
import { parseBigIntAsNumber } from "../utils/format"
import { createLogger } from "../utils/logger"
import { BackendError, retryable } from "../utils/utils"

const logger = createLogger("POOL-API")

export type RefreshFunction = () => Promise<void>

/**
 * Get the price of a token, from a pool
 */
export async function getSwapOutput(
  id: string,
  quantity: bigint,
  pool: string,
  swapper: string,
  ao: AoInstance,
) {
  const res = await ao.dryrun({
    process: pool,
    tags: [
      { name: "Action", value: "Get-Swap-Output" },
      { name: "Token", value: id },
      { name: "Quantity", value: quantity.toString() },
      { name: "Swapper", value: swapper },
    ],
  })

  for (const msg of res.Messages as Message[]) {
    const val = getTagValue("Output", msg.Tags)

    if (val) return BigInt(val)

    // also possible to use
    // ["Quantity-After-Fees"] - input qty minus fees
    // ["LP-Fee-Quantity"]
    // ["Protocol-Fee-Quantity"]
    // ["Fee-Discount-Tier"] - "portfolio-agent", etc
  }

  return 0n
}

/**
 * Fee percentage hook (in basis points)
 */
export function useFeePercentage(pool?: string): [bigint, boolean] {
  const poolDetails = usePoolDetails(pool as string)

  if (!poolDetails.data) return [25n, true]

  return [poolDetails.data.fee, false]
}

export async function getReserves(
  pool: string,
  pair: [string, string],
  ao: AoInstance,
): Promise<Reserves> {
  const dataSource = getDataSource(ao)
  const reservesArray = await dataSource.getReserves(pool, pair)

  console.log("onReservesUpdate", {
    pool,
    pair,
    reserves: reservesArray,
  })

  return {
    [pair[0]]: reservesArray[0],
    [pair[1]]: reservesArray[1],
  }
}

export type Reserves = { [token: string]: bigint }

/**
 * Reserves hook
 */
export function useReserves(pool?: string, tokenA?: string, tokenB?: string) {
  // ao instance
  const ao = useAo()

  console.log("Fetching reserves for pool:", pool, "tokens:", tokenA, tokenB)

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ["reserves", pool, ao],
    queryFn: async (): Promise<Reserves> => {
      console.log("Fetching reserves for pool:", pool, "tokens:", tokenA, tokenB)
      if (!pool || !tokenA || !tokenB) return {} as Reserves
      try {
        return await getReserves(pool, [tokenA, tokenB], ao)
      } catch (error) {
        console.error("Error fetching reserves:", error)
        throw error // Let React Query handle retries
      }
    },
    enabled: !!pool && !!tokenA && !!tokenB,
    retry: 5, // Override default retry count for this specific query
    retryDelay: (attemptIndex) => {
      // Faster retry strategy
      const baseDelay = 500 // Start at 500ms
      const delay = Math.min(baseDelay * 1.5 ** attemptIndex, 5000)
      const jitter = delay * 0.1 * Math.random()
      return delay + jitter
    },
  })

  return [data, isLoading || isRefetching, refetch] as const
}

export type PendingProvide = {
  provideId: string
  poolId: string
  token: string
  quantity: bigint
  provideFound: boolean
}

/**
 * Get pending provide
 */
export async function getPendingProvide(
  pool: string,
  activeAddr: string,
  ao: AoInstance,
): Promise<PendingProvide | undefined> {
  const result = await ao.dryrun({
    Id: "0000000000000000000000000000000000000000003",
    Owner: activeAddr,
    process: pool,
    tags: [{ name: "Action", value: "Get-Pending-Provides" }],
  })

  const messages = result.Messages as Message[]

  const response = messages.find(
    (msg) => flattenTags(msg.Tags)["Action"] === "Get-Pending-Provides-Response",
  )

  if (!response) return

  const tags = flattenTags(response.Tags)

  return {
    token: tags["Token"],
    quantity: BigInt(tags["Quantity"]),
    provideFound: tags["Provide-Found"] === "true",
    provideId: tags["Provide-Id"],
    poolId: pool,
  }
}

export function usePendingProvide(pool?: string) {
  // ao instance
  const ao = useAo()

  // active address
  const activeAddr = useActiveAddress()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["pending-provide", pool, activeAddr, ao],
    queryFn: async () => {
      if (!pool || !activeAddr) return
      return await getPendingProvide(pool, activeAddr, ao)
    },
    enabled: !!pool,
  })

  return [data, isLoading, refetch] as const
}

/**
 * Calculate price impact
 */
export function getPriceImpact(token: string, quantity: bigint, reserves: Reserves) {
  const otherToken = Object.keys(reserves).find((t) => t !== token)

  if (!otherToken || !reserves[token]) return "0"

  const K = reserves[otherToken] * reserves[token]

  const denomination = 18

  const out = reserves[otherToken] - K / (reserves[token] + quantity)
  const oldPrice = (reserves[otherToken] * 10n ** BigInt(denomination)) / reserves[token]
  const newPrice =
    ((reserves[otherToken] - out) * 10n ** BigInt(denomination)) / (reserves[token] + quantity)

  if (oldPrice === 0n) return "0"

  const priceImpact = ((newPrice - oldPrice) * 100n * 10n ** BigInt(denomination)) / oldPrice

  console.log("onPriceImpactUpdate", {
    priceImpact,
    oldPrice,
    newPrice,
  })

  return parseBigIntAsNumber(priceImpact, denomination, 2)
}

export const wait = (time: number) => new Promise((resolve) => setTimeout(resolve, time))

/**
 * SU process messages query result
 */
export interface SUProcessMessages {
  edges: {
    cursor: string
    node: {
      message: DataItem
      assignment: DataItem
    }
  }[]
  page_info: {
    has_next_page: boolean
  }
}

/**
 * Message in SU process
 */
export interface DataItem {
  anchor: string | null
  id: string
  owner: {
    address: string
    key: string
  }
  signature: string
  tags: Tag[]
  target: string
}

export interface SwapParams {
  /** Input qty */
  input: bigint
  /** Expected output */
  minOutput: bigint
  /** Input token */
  token: string
  /** Pool process */
  pool: string
}

/**
 * Execute swap
 */
export async function swap(data: SwapParams, ao: AoInstance) {
  const swapNonce = `${Date.now()}-${Math.floor(Math.random() * 1000000000)}`
  // swap message tags
  const swapTags = [
    { name: "X-Expected-Min-Output", value: data.minOutput.toString() },
    { name: "X-Swap-Nonce", value: swapNonce },
    { name: "X-Action", value: "Swap" },
  ]

  // transfer the tokens and add forwarded tags
  // to let the process know about the order
  const transferID = await transfer(
    {
      token: data.token,
      recipient: data.pool,
      quantity: data.input,
      tags: swapTags,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )

  return transferID
}

/**
 * Execute MultiHop Swap
 */
export interface MultiHopSwapParams {
  simpleHopperAddress: string
  /** Input qty */
  input: bigint
  /** Swap Route*/
  swapRoute: SwapRouteResult
  token: string
}

export async function swapMultiHop(data: MultiHopSwapParams, ao: AoInstance) {
  // swap message tags
  const swapTags = [
    { name: "X-Action", value: "Multi-Hop-Swap" },
    { name: "X-Path", value: JSON.stringify(data.swapRoute.dijkstra_route.path) },
    {
      name: "X-Pool-Addresses",
      value: JSON.stringify(data.swapRoute.dijkstra_route.pool_addresses),
    },
  ]

  // transfer the tokens and add forwarded tags
  // to let the process know about the order
  const transferID = await transfer(
    {
      token: data.token,
      recipient: data.simpleHopperAddress,
      quantity: data.input,
      tags: swapTags,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )

  return transferID
}

export async function simulateMultiHop(
  swapRoute: SwapRouteResult,
  swapper: string,
  allowedSlippage: number,
  ao: AoInstance,
) {
  const { path, pool_addresses, amounts } = swapRoute.dijkstra_route

  console.log("[dd] [sim] Simulating hops")
  const hopPromises = pool_addresses.map((pool, i) => {
    const inputToken = path[i]
    const inputAmount = BigInt(amounts[i])
    const expectedOutput = BigInt(amounts[i + 1])
    return getSwapOutput(inputToken, inputAmount, pool, swapper, ao).then((simulatedOutput) => ({
      i,
      pool,
      inputToken,
      nextToken: path[i + 1],
      simulatedOutput,
      expectedOutput,
    }))
  })

  const results = await Promise.all(hopPromises)

  for (const result of results) {
    const { i, simulatedOutput, expectedOutput, inputToken, nextToken, pool } = result
    const diff =
      simulatedOutput > expectedOutput
        ? simulatedOutput - expectedOutput
        : expectedOutput - simulatedOutput
    const diffPercentage = Number(diff * 100n) / Number(expectedOutput)

    console.log(
      `[dd] [sim] Hop ${i}: ${inputToken} -> ${nextToken} via ${pool}: simulated ${simulatedOutput} vs expected ${expectedOutput} (diff ${diffPercentage.toFixed(
        2,
      )}%)`,
    )

    if (diffPercentage > allowedSlippage) {
      throw new Error(
        `Simulation mismatch at hop ${i}: difference of ${diffPercentage.toFixed(
          2,
        )}% exceeds allowed slippage of ${allowedSlippage}%`,
      )
    } else if (diffPercentage > 0) {
      console.warn(
        `[dd] [sim]  Warning at hop ${i}: simulated output differs by ${diffPercentage.toFixed(
          2,
        )}% (within allowed slippage).`,
      )
    }
  }

  console.log("[dd] [sim] Multi-hop simulation successful.")
}

/**
 * ready = result is ready
 */
export type OrderStatus =
  | "preparing"
  | "pendingWalletConfirmation"
  | "pendingSimulation"
  | "pending"
  | "error"
  | "success"

/**
 * Fetch the result of a swap message
 */
export async function readSwapResult(orderID: string): Promise<[bigint, string]> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, orderID)

  const error = messages.find((msg) => msg.tags["Action"] === "Order-Error")
  if (error) throw new BackendError(error.tags["Result"])

  const confirmation = messages.find((msg) => msg.tags["Action"] === "Order-Confirmation")
  if (!confirmation) throw new Error("Confirmation not found")

  const tokensReceived = confirmation.tags["To-Quantity"]
  if (!tokensReceived) throw new BackendError("Confirmation response malformed")

  console.log("onConfirmationId", confirmation.id)

  return [BigInt(tokensReceived), confirmation.id]
}

/**
 * Fetch the result of a swap message
 */
export async function readSwapResultMultiHop(
  orderID: string,
  token: string,
): Promise<[bigint, string]> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, orderID)

  const error = messages.find((msg) => msg.tags["Action"] === "Order-Error")
  if (error) throw new BackendError(error.tags["Result"])

  const confirmations = messages.filter((msg) => msg.tags["Action"] === "Order-Confirmation")
  if (!confirmations.length) throw new Error("Confirmation not found")

  const tokenConfirmation = confirmations.find((msg) => msg.tags["To-Token"] === token)
  if (!tokenConfirmation) throw new Error("Confirmation for end-token not found")

  const tokensReceived = tokenConfirmation.tags["To-Quantity"]
  if (!tokensReceived) throw new BackendError("Confirmation response malformed")

  console.log("onConfirmationId", tokenConfirmation.id)

  return [BigInt(tokensReceived), tokenConfirmation.id]
}

interface TokenParam {
  token: string
  quantity: bigint
}

/**
 * Calculates the amount of pool tokens earned for a provide
 */
export function calculateProvideEarnings({ qtyA, qtyB, lpTotalSupply, reserveA }: EarningsData) {
  // non-0 liquidity
  if (reserveA && reserveA > 0n && lpTotalSupply > 0n) {
    return (lpTotalSupply * qtyA) / reserveA
  }

  // initial provide / 0 liquidity
  // sqrt with the babylonian/Heron's method
  // we use the same algorithm in the AMM
  let x = qtyA * qtyB

  // handle trivial cases
  if (x === 0n) return 0n
  if (x <= 3n) return 1n

  let res = x
  let next = x / 2n + 1n

  while (next < res) {
    res = next
    next = (x / next + next) / 2n
  }

  return res
}

interface EarningsData {
  qtyA: bigint
  qtyB: bigint
  lpTotalSupply: bigint
  reserveA?: bigint
}

export interface ProvideParams {
  /** TokenA details */
  tokenA: TokenParam
  /** TokenB details */
  tokenB: TokenParam
  /** Pool process */
  pool: string
  /** Slippage tolerance percentage */
  slippageTolerance: number
}

/**
 * Liquidity provide interaction
 */
export async function provide(
  data: ProvideParams,
  ao: AoInstance,
  onFirstTxSigned?: () => void,
): Promise<[string, string]> {
  // create signer
  const signer = createDataItemSigner(window.arweaveWallet)

  // tags appended to the transfer
  const poolTags = [
    { name: "X-Action", value: "Provide" },
    { name: "X-Slippage-Tolerance", value: data.slippageTolerance.toString() },
  ]

  const firstTransfer = await transfer(
    {
      ...data.tokenA,
      recipient: data.pool,
      tags: poolTags,
    },
    signer,
    ao,
  )

  console.log("onFirstTransfer", firstTransfer)

  onFirstTxSigned?.()

  const secondTransfer = await transfer(
    {
      ...data.tokenB,
      recipient: data.pool,
      tags: poolTags,
    },
    signer,
    ao,
  )

  console.log("onSecondTransfer", secondTransfer)

  return [firstTransfer, secondTransfer]
}

export interface PartialProvideParams {
  token: string
  quantity: bigint
  pool: string
  slippageTolerance: number
}

export async function partialProvide(data: PartialProvideParams, ao: AoInstance) {
  // create signer
  const signer = createDataItemSigner(window.arweaveWallet)

  // tags appended to the transfer
  const poolTags = [
    { name: "X-Action", value: "Provide" },
    { name: "X-Slippage-Tolerance", value: data.slippageTolerance.toString() },
  ]

  const transferId = await transfer(
    {
      token: data.token,
      quantity: data.quantity,
      recipient: data.pool,
      tags: poolTags,
    },
    signer,
    ao,
  )

  console.log("onSecondTransfer", transferId)

  return transferId
}

export async function cancelProvide(pendingProvide: PendingProvide, ao: AoInstance) {
  // create signer
  const signer = createDataItemSigner(window.arweaveWallet)

  const messageId = await ao.message({
    process: pendingProvide.poolId,
    signer,
    tags: [
      { name: "Action", value: "Cancel" },
      { name: "Transfer", value: pendingProvide.provideId },
    ],
  })

  return messageId
}

/**
 * Read the provide interactions result
 */
export async function readProvideResult(
  transferA: string,
  transferB?: string,
): Promise<[bigint, string]> {
  // Get messages linked to both transfers
  const transferIds = [transferA, transferB].filter(Boolean) as string[]

  let allMessages: AoMessage[] = []
  let error: AoMessage | undefined
  let confirmation: AoMessage | undefined

  // Check messages linked to each transfer ID
  for (const transferId of transferIds) {
    const [, messages] = await getLinkedMessages(undefined, undefined, false, transferId)
    allMessages = [...allMessages, ...messages]

    // Look for error messages
    if (!error) {
      error = messages.find((msg) => msg.tags["Action"] === "Provide-Error")
    }

    // Look for confirmation messages
    if (!confirmation) {
      confirmation = messages.find((msg) => msg.tags["Action"] === "Provide-Confirmation")
    }

    // If we found what we need, we can break early
    if (error || confirmation) break
  }

  if (error) throw new BackendError(error.tags["Result"])

  if (!confirmation) throw new Error("Confirmation not found")

  const lpReceived = confirmation.tags["Received-Pool-Tokens"]
  if (!lpReceived) throw new Error("Confirmation response malformed")

  console.log("onConfirmationId", confirmation.id)

  return [BigInt(lpReceived), confirmation.id]
}

/**
 * Burn pool tokens interaction
 */
export async function burn(quantity: bigint, pool: string, ao: AoInstance) {
  // create signer
  const signer = createDataItemSigner(window.arweaveWallet)

  // now send the burn interaction
  const messageId = await ao.message({
    process: pool,
    signer,
    tags: [
      { name: "Action", value: "Burn" },
      { name: "Quantity", value: quantity.toString() },
    ],
  })

  return messageId
}

/**
 * Read burn interaction result
 */
export async function readBurnResult(burnMessageId: string) {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, burnMessageId)

  const error = messages.find((msg) => msg.tags["Action"] === "Burn-Error")
  if (error) throw new BackendError(error.tags["Result"])

  const confirmation = messages.find((msg) => msg.tags["Action"] === "Burn-Confirmation")
  if (!confirmation) throw new Error("Confirmation not found")

  const withdrawals: { [token: string]: bigint } = {}

  for (const tagName in confirmation.tags) {
    if (!tagName.startsWith("Withdrawn-")) continue
    withdrawals[tagName.replace("Withdrawn-", "")] = BigInt(confirmation.tags[tagName])
  }

  return [withdrawals, confirmation.id] as const
}

const OrderConfirmationCountQuery = gql`
  query ($poolId: String!) {
    transactions(
      sort: INGESTED_AT_DESC

      tags: [
        { name: "Action", values: ["Order-Confirmation"] },
        { name: "From-Process", values: [$poolId] },
        { name: "Data-Protocol", values: ["ao"] }
      ]
      ${AO_MIN_INGESTED_AT}
    ) {
      count
    }
  }
`

export async function getOrderConfirmationCount(poolId: string): Promise<number> {
  const result = await goldsky
    .query<TransactionsResponse>(OrderConfirmationCountQuery, { poolId })
    .toPromise()

  return result?.data?.transactions?.count as number
}

async function readCreateConfirmation(createId: string): Promise<string> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, createId)

  const error = messages.find((msg) => msg.tags["Action"] === "Add-Pool-Error")
  if (error) throw new BackendError(error.tags["Error"])

  const confirmation = messages.find((msg) => msg.tags["Action"] === "Add-Pool-Confirmation")
  if (!confirmation) throw new Error("Create Pool Confirmation not found")

  return confirmation.id
}

async function readDexiRegistrationStatus(poolId: string, ao: AoInstance): Promise<string> {
  const messageResult = await ao.dryrun({
    process: ENV.VITE_DEXI_PROCESS,
    tags: [
      {
        name: "Action",
        value: "Get-AMM-Registration-Status",
      },
      {
        name: "Process-Id",
        value: poolId,
      },
    ],
  })

  if (messageResult.Error) {
    throw new BackendError(messageResult.Error)
  }

  if (messageResult.Messages[0].Tags) {
    const Status = messageResult.Messages[0].Tags.find((tag: Tag) => tag.name === "Status")

    if (!Status) {
      throw new BackendError("Status not found in the response.")
    }

    return Status.value
  }

  throw new BackendError("Error fetching data.")
}

async function readSubConfirmation(poolId: string, ao: AoInstance): Promise<string> {
  const status = await readDexiRegistrationStatus(poolId, ao)

  if (status !== "paid--complete") {
    throw new Error(`Dexi registration not complete: ${status}`)
  }

  return status
}

export async function create(
  tokenA: string,
  tokenB: string,
  feePercentageBps: bigint,
  ao: AoInstance,
) {
  const addPoolMessageId = await ao.message({
    process: AMM_FACTORY,
    tags: [
      { name: "Action", value: "Add-Pool" },
      { name: "Token-A", value: tokenA },
      { name: "Token-B", value: tokenB },
      {
        name: "Fee-Bps",
        value: feePercentageBps.toString(),
      },
      {
        name: "Name",
        value: `Botega v2 Pool ${tokenA.substring(0, 6)}...${tokenB.substring(0, 6)}`,
      },
    ],
    data: "",
    signer: createDataItemSigner(window.arweaveWallet),
  })
  console.log("onAddPoolMessageId", addPoolMessageId)

  const confirmationId = await retryable(readCreateConfirmation)(addPoolMessageId)
  console.log("onAddPoolConfirmationId", confirmationId)

  const { poolId } = await retryable(getPoolByTokens)([tokenA, tokenB], ao, feePercentageBps)
  console.log("onPoolId", poolId)

  // wait for Dexi to subscribe to the pool
  const registrationStatus = await retryable(readSubConfirmation)(poolId, ao)
  console.log("onRegistrationStatus", registrationStatus)

  if (!poolId) throw new Error("Something went terribly wrong")

  // Set the cron for the process
  try {
    const dataSource = getDataSource(ao)
    await dataSource.setCronForProcess(poolId, "1-minute")

    console.log("Cron set for process", poolId)
  } catch (error) {
    console.error("Error setting cron for process", poolId, error)
  }

  return poolId
}

export async function getPoolInfo(pool: string, ao: AoInstance, _maxTries = MAX_TRIES) {
  const dataSource = getDataSource(ao)

  const poolInfo = await dataSource.getPoolInfo(pool)

  if (!poolInfo) {
    throw new Error(`Pool info not found for pool: ${pool}`)
  }

  return poolInfo
}

export async function getUserBalance(
  pool: string,
  user: string,
  ao: AoInstance,
  _maxTries = MAX_TRIES,
): Promise<bigint> {
  const dataSource = getDataSource(ao)

  const balance = await dataSource.getBalance(pool, user)

  if (balance === undefined) {
    throw new Error(`Balance not found for pool: ${pool} and user: ${user}`)
  }

  return balance
}

export async function getPoolPnlHistory(
  user: string,
  _ao: AoInstance,
  maxTries = MAX_TRIES,
): Promise<PoolPnlHistory> {
  let tries = 0
  let lastError: unknown

  while (tries < maxTries) {
    try {
      // Call Supabase Edge Function via fetch
      const supabaseUrl = (ENV.VITE_SUPABASE_URL ?? "").replace(/\/$/, "")
      const supabaseKey = ENV.VITE_SUPABASE_ANON_KEY

      if (!supabaseUrl || !supabaseKey) {
        throw new Error("Supabase environment variables are not set")
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/pool-token-pnl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ user }),
      })

      if (!response.ok) {
        throw new Error(`Supabase pool-token-pnl error! status: ${response.status}`)
      }

      const data: PoolPnlHistory = await response.json()

      if (data) {
        return data
      }

      throw new Error(`No response from pool-token-pnl for user: ${user}`)
    } catch (error) {
      console.error(error)
      lastError = error instanceof Error ? error.message : error
      tries++
    }

    await wait(RETRY_INTERVAL)
  }

  if (typeof lastError === "string") {
    throw new Error(lastError)
  }

  throw lastError
}
